import React, { useCallback, useMemo, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";

import { CreateProject as CreateProjectPermission } from "../../../services/authorization/Permissions";
import ProjectInput, { CustomPropertyValue } from "../../../services/project/ProjectInput";
import { dateService } from "../../../services/date-service/DateService";
import { currentUserService } from "../../../services/user/CurrentUserStorage";
import PageLayout from "../../_layout/PageLayout";
import Form from "../../../components/form/Form";
import { EditProjectName, EditProjectStatus, EditProjectLead } from "../components/DataComponents";
import DisplayDateCreated from "../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../components/data-components/DisplayCreatedBy";
import {
  DataSection,
  SubSection,
  EditDescriptionSection,
} from "../../../components/data-components/sections/DataSections";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { FormikHelpers, FormikValues } from "formik";
import { EditCoordinateSystem } from "../../settings/gis-coordinates/components/DataComponents";
import { EditAvailableFromAndDeadlineDates } from "../components/DataComponents";
import { EditProjectFormat } from "../components/EditProjectFormat";
import { EditProjectCodeSystem } from "../components/EditProjectCodeSystem";
import { ProjectFormat } from "../../../services/project/ProjectFormat";
import { ProjectCodeSystem } from "../../../services/project/ProjectCodeSystem";
import { api } from "../../../services/api/ApiService";
import { ProjectsStorageModel } from "../../../services/project/ProjectsStorage";
import { GisCoordinatesStorageModel } from "../../../services/organization/GisCoordinatesStorage";
import { ProjectPropertiesStorageModel } from "../../../services/organization/ProjectPropertyStorage";
import { EditCustomPropertiesSection } from "../project-details/edit-project/components/DataComponents";
import { ProjectMenuItemsModel } from "../ProjectMenuItems";
import {
  ProjectFormatCodeSystemServiceModel,
  emptyProjectCodeSystem,
} from "../../../services/project/ProjectFormatCodeSystemService";
import { EditReportTemplate, emptyReportTemplate } from "../components/EditReportTemplate";
import { featureFlagsStorage } from "../../../services/feature-flags/FeatureFlagsStorage";
import { warningLeavePageService } from "../../../services/page/WarningLeavePageService";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import { EditProjectLead as EditProjectLeadPermission } from "../../../services/authorization/Permissions";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";
import { ProjectStatus } from "../../../generated/Projects";
import { Flags } from "../../../generated/FeatureFlags";

export default observer(() => {
  const history = useHistory();
  const { handleErrorInSnackbar } = useErrorHandler();

  const [projectsStorage] = useState(() =>
    ProjectsStorageModel.create({ autoLoad: false }, { api })
  );
  const [projectPropertiesStorage] = useState(() =>
    ProjectPropertiesStorageModel.create({}, { api })
  );
  const [projectFormatCodeSystemService] = useState(() =>
    ProjectFormatCodeSystemServiceModel.create({}, { api })
  );
  const [gisCoordinateStorage] = useState(() => GisCoordinatesStorageModel.create({}, { api }));

  const menuItems = useMemo(() => {
    const projectMenuItems = ProjectMenuItemsModel.create({
      createMode: true,
    });

    return {
      staticItems: projectMenuItems.getStaticItems(),
    };
  }, []);

  const isPageLoading = useMemo(
    () =>
      gisCoordinateStorage.isLoading ||
      projectFormatCodeSystemService.formatsAreLoading ||
      !currentUserService.currentUser.id ||
      projectPropertiesStorage.isLoading,
    [
      gisCoordinateStorage.isLoading,
      projectFormatCodeSystemService.formatsAreLoading,
      projectPropertiesStorage.isLoading,
    ]
  );

  const initialValues = useMemo(() => {
    return {
      name: "",
      status: {
        id: ProjectStatus.Open,
        name: "Open",
      },
      leadId: currentUserService.currentUser.id,
      lead: { id: currentUserService.currentUser.id, name: currentUserService.currentUser.name },
      availableFrom: dateService.currentDate,
      deadline: null,
      description: "",
      gisCoordinatesId: gisCoordinateStorage.gisCoordinateDetails.id || null,
      coordinateSystemName: {
        id: gisCoordinateStorage.gisCoordinateDetails.id,
        name: gisCoordinateStorage.gisCoordinateDetails.name,
      },
      coordinateSystemWKID: {
        id: gisCoordinateStorage.gisCoordinateDetails.id,
        name: gisCoordinateStorage.gisCoordinateDetails.wkid,
      },
      format: {},
      codeSystem: {},
      reportTemplate: emptyReportTemplate,
      customPropertyValues: projectPropertiesStorage.initialValues,
    };
  }, [
    gisCoordinateStorage.gisCoordinateDetails.id,
    gisCoordinateStorage.gisCoordinateDetails.name,
    gisCoordinateStorage.gisCoordinateDetails.wkid,
    projectPropertiesStorage.initialValues,
  ]);

  const goToProjectList = useCallback(() => history.push("/projects"), [history]);

  const handleSubmit = useCallback(
    async (values: FormikValues, formActions: FormikHelpers<FormikValues>) => {
      const availableFrom = dateService.dateToUTC(values.availableFrom);

      let deadline: any = null;
      if (values.deadline) deadline = dateService.dateToUTC(values.deadline);

      const customPropertyValuesList: CustomPropertyValue[] = [];

      Object.entries(values.customPropertyValues).forEach((entry) => {
        var [label, value] = entry;
        const property = {
          label,
          value: value as string,
        };
        customPropertyValuesList.push(property);
      });

      const project: ProjectInput = {
        name: values.name,
        status: Number(values.status.id),
        leadId: values.leadId,
        timeline: {
          availableFrom: availableFrom,
          deadline: deadline,
        },
        description: values.description,
        gisCoordinatesId: values.gisCoordinatesId,
        formatId: projectFormatCodeSystemService.selectedProjectFormat.id,
        codeSystemId: projectFormatCodeSystemService.selectedProjectCodeSystem.id,
        customPropertyValues: customPropertyValuesList,
        reportTemplateId: values.reportTemplate?.id !== "" ? values.reportTemplate?.id : undefined,
      };
      try {
        await projectsStorage.createProject(project);
        warningLeavePageService.setIsDirty(false);
        return goToProjectList();
      } catch (error) {
        const applicationError = error as ApplicationError;
        applicationError.withForm(values, formActions);
        applicationError.withErrorMessage("default-error.project.create");
        handleErrorInSnackbar(applicationError);
      }
    },
    [
      goToProjectList,
      handleErrorInSnackbar,
      projectFormatCodeSystemService.selectedProjectCodeSystem.id,
      projectFormatCodeSystemService.selectedProjectFormat.id,
      projectsStorage,
    ]
  );

  useEffect(() => {
    async function setupProjectFormatAndCodeSystemInitialData() {
      await projectFormatCodeSystemService.setupCreateProjectFormatAndCodeSystemInitialData();
    }

    setupProjectFormatAndCodeSystemInitialData();
  }, [projectFormatCodeSystemService]);

  useEffect(() => {
    async function renewCodeSystems() {
      if (projectFormatCodeSystemService.selectedProjectFormat.isValid) {
        projectFormatCodeSystemService.renewCodeSystems(
          projectFormatCodeSystemService.selectedProjectFormat.id
        );
      }
    }
    renewCodeSystems();
  }, [projectFormatCodeSystemService, projectFormatCodeSystemService.selectedProjectFormat]);

  useEffect(() => {
    breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
  }, [history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[CreateProjectPermission]}
      titleKey="CreateProject.add-new"
      id="create-project"
      menuItems={menuItems}
      isLoading={isPageLoading}
    >
      <Form
        formikOptions={{
          initialValues: initialValues,
          onSubmit: handleSubmit,
          validateOnBlur: false,
          validateOnChange: false,
        }}
        onCancel={goToProjectList}
      >
        {({ values, setFieldValue }) => {
          return (
            <>
              <DataSection labelKey="general.projects.generic-details">
                <SubSection>
                  <EditProjectName name={values.name} />
                  <EditProjectStatus status={values.status} setFieldValue={setFieldValue} />
                  <EditProjectLead
                    lead={values.lead}
                    leadId={values.leadId}
                    setFieldValue={setFieldValue}
                    disabled={!permissionsService.userHasPermissionTo(EditProjectLeadPermission)}
                  />
                </SubSection>

                <SubSection>
                  <EditProjectFormat
                    value={projectFormatCodeSystemService.selectedProjectFormat}
                    formats={projectFormatCodeSystemService.projectFormats}
                    onChange={(newProjectFormat: ProjectFormat) => {
                      projectFormatCodeSystemService.setSelectedProjectFormat(newProjectFormat);
                      projectFormatCodeSystemService.setSelectedProjectCodeSystem(
                        emptyProjectCodeSystem
                      );
                    }}
                    setFieldValue={setFieldValue}
                  />
                  <EditProjectCodeSystem
                    value={projectFormatCodeSystemService.selectedProjectCodeSystem}
                    codeSystems={projectFormatCodeSystemService.codeSystems}
                    disabled={projectFormatCodeSystemService.codeSystemDisabled}
                    onChange={(newProjectCodeSystem: ProjectCodeSystem) =>
                      projectFormatCodeSystemService.setSelectedProjectCodeSystem(
                        newProjectCodeSystem
                      )
                    }
                    setFieldValue={setFieldValue}
                  />
                  <EditCoordinateSystem
                    name={values.coordinateSystemName}
                    WKID={values.coordinateSystemWKID}
                    setFieldValue={setFieldValue}
                    areFieldsRequired={true}
                  />
                </SubSection>

                <SubSection>
                  <EditAvailableFromAndDeadlineDates
                    availableFrom={values.availableFrom}
                    deadline={values.deadline}
                    setFieldValue={setFieldValue}
                  />
                  {featureFlagsStorage.isFeatureEnabled(
                    Flags.Use_Custom_Report_Template_For_Project
                  ) && (
                    <EditReportTemplate
                      value={values.reportTemplate}
                      setFieldValue={setFieldValue}
                    />
                  )}
                </SubSection>

                <SubSection>
                  <DisplayDateCreated />
                  <DisplayCreatedBy />
                </SubSection>
              </DataSection>

              {projectPropertiesStorage.itemsCount > 0 && (
                <EditCustomPropertiesSection
                  labelKey="general.projects.custom-details"
                  properties={projectPropertiesStorage.mapCustomProjectProperties()}
                  values={values.customPropertyValues}
                  setFieldValue={setFieldValue}
                  nestedFieldsPath="customPropertyValues"
                />
              )}
              <EditDescriptionSection description={values.description} />
            </>
          );
        }}
      </Form>
    </PageLayout>
  );
});
